import type { TFunction } from "i18next";
import { CalendarRange, Clock, Gauge, Sparkles, UserRoundCheck, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { useDashboard, useTeamStatus, type Dashboard, type PeriodTotals } from "@/api/queries";
import { useVacationText } from "@/features/vacations/useVacationText";
import { isStaff, useCurrentUser } from "@/app/auth";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { WeekChart } from "@/components/common/WeekChart";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Segmented } from "@/components/ui/segmented";
import { TeamSelect, useScopeTeams } from "@/features/activity/TeamSelect";
import { errorText } from "@/lib/errors";
import { formatDuration, formatLongDate, formatTime, formatWeekRange, percentOf } from "@/lib/format";

import { AttentionCard } from "./AttentionCard";
import { ServerStatusCard } from "./ServerStatusCard";
import { TeamTable } from "./TeamTable";
import { TopAppsCard } from "./TopAppsCard";

type View = "team" | "me";

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const me = useCurrentUser();
  const staff = isStaff(me);
  const [params, setParams] = useSearchParams();
  const view: View = staff && params.get("view") !== "me" ? "team" : "me";
  const teamId = (staff && params.get("team")) || undefined;
  const dash = useDashboard(staff ? teamId : undefined);
  const teams = useScopeTeams();

  const update = (next: { view?: View; team?: string }) => {
    const p = new URLSearchParams(params);
    if (next.view !== undefined) {
      if (next.view === "me") p.set("view", "me");
      else p.delete("view");
    }
    if ("team" in next) {
      if (next.team) p.set("team", next.team);
      else p.delete("team");
    }
    setParams(p, { replace: true });
  };

  const viewOptions: { value: View; label: string }[] = [
    { value: "team", label: t("dashboard.views.team") },
    { value: "me", label: t("dashboard.views.me") },
  ];
  const teamName = teams.find((x) => x.id === teamId)?.name;
  const date = dash.data ? formatLongDate(dash.data.today, lang) : "";
  const description =
    view === "team"
      ? [teamName ?? t("scope.allPeople"), date].filter(Boolean).join(" · ")
      : t("dashboard.subtitleEmployee");

  return (
    <>
      <PageHeader
        title={t("dashboard.title")}
        description={description}
        actions={
          staff && (
            <>
              {view === "team" && <TeamSelect value={teamId} onChange={(team) => update({ team })} />}
              <Segmented
                aria-label={t("dashboard.view")}
                value={view}
                onChange={(v) => update({ view: v })}
                options={viewOptions}
              />
            </>
          )
        }
      />
      {dash.isError && (
        <Alert variant="danger" className="mb-4 items-center">
          <span className="flex-1">{errorText(t, dash.error)}</span>
          <Button variant="secondary" size="sm" onClick={() => void dash.refetch()}>
            {t("common.retry")}
          </Button>
        </Alert>
      )}
      {view === "team" ? (
        <TeamView data={dash.data} teamId={teamId} lang={lang} />
      ) : (
        <PersonalView data={dash.data} lang={lang} />
      )}
    </>
  );
}

/** Tracked and approved manual time. */
function worked(totals: PeriodTotals): number {
  return totals.tracked_seconds + totals.manual_seconds;
}

function manualHint(t: TFunction, totals: PeriodTotals, lang: string): string | undefined {
  return totals.manual_seconds > 0
    ? t("dashboard.manualHint", { time: formatDuration(totals.manual_seconds, lang) })
    : undefined;
}

function productiveHint(t: TFunction, totals: PeriodTotals): string | undefined {
  return totals.tracked_seconds > 0
    ? t("dashboard.unproductiveHint", {
        percent: percentOf(totals.unproductive_seconds, totals.tracked_seconds),
      })
    : undefined;
}

function TeamView({ data, teamId, lang }: { data: Dashboard | undefined; teamId?: string; lang: string }) {
  const { t } = useTranslation();
  const status = useTeamStatus(teamId);
  const items = status.data?.items ?? [];
  const team = data?.team;
  const working = items.filter((m) => m.state === "working").length;
  const idle = items.filter((m) => m.state === "idle").length;
  const kpi = (value: string | undefined) => value ?? <Skeleton className="h-8 w-24" />;
  const members = team?.members ?? 0;
  const appsHref = `/activity/apps?period=week${teamId ? `&team=${teamId}` : ""}`;
  const pending = team?.pending_manual_time ?? 0;
  const away = team?.on_vacation ?? 0;

  return (
    <div className="flex flex-col gap-6">
      {pending > 0 && (
        <Alert variant="warning" className="items-center">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex-1">{t("dashboard.pendingManual", { count: pending })}</span>
            <Button asChild variant="secondary" size="sm">
              <Link to="/timesheets/manual?view=review">{t("dashboard.review")}</Link>
            </Button>
          </div>
        </Alert>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t("dashboard.kpi.workingNow")}
          icon={UserRoundCheck}
          value={
            status.isPending ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                {working}{" "}
                <span className="text-base font-medium text-muted">
                  {t("dashboard.ofTotal", { total: items.length })}
                </span>
              </>
            )
          }
          hint={
            [
              idle > 0 ? t("dashboard.idleHint", { count: idle }) : "",
              away > 0 ? t("dashboard.vacationHint", { count: away }) : "",
            ]
              .filter(Boolean)
              .join(" · ") || undefined
          }
        />
        <StatCard
          label={t("dashboard.kpi.hoursToday")}
          icon={Clock}
          value={kpi(team && formatDuration(worked(team.today), lang))}
          hint={
            team && members > 0
              ? [
                  t("dashboard.perPerson", { time: formatDuration(worked(team.today) / members, lang) }),
                  manualHint(t, team.today, lang),
                ]
                  .filter(Boolean)
                  .join(" · ")
              : undefined
          }
        />
        <StatCard
          label={t("dashboard.kpi.avgActivity")}
          icon={Gauge}
          value={kpi(team && (team.today.tracked_seconds > 0 ? `${team.today.activity_percent}%` : "—"))}
          hint={t("dashboard.todayHint")}
        />
        <StatCard
          label={t("dashboard.kpi.productive")}
          icon={Sparkles}
          value={kpi(
            team &&
              (team.today.tracked_seconds > 0
                ? `${percentOf(team.today.productive_seconds, team.today.tracked_seconds)}%`
                : "—"),
          )}
          hint={team && productiveHint(t, team.today)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle>{t("dashboard.teamWeek")}</CardTitle>
            <CardDescription>
              {data
                ? t("dashboard.weekDescription", {
                    range: formatWeekRange(data.week_start, lang),
                    total: formatDuration(team ? worked(team.week) : 0, lang),
                  })
                : " "}
            </CardDescription>
          </CardHeader>
          <div className="px-5 pb-5">
            {data && team ? (
              <WeekChart days={team.days} projects={data.projects} today={data.today} lang={lang} />
            ) : (
              <Skeleton className="h-64 w-full" />
            )}
          </div>
        </Card>
        <AttentionCard items={team?.attention} lang={lang} />
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex-row items-start justify-between gap-3 pb-4">
          <div>
            <CardTitle>{t("dashboard.teamTitle")}</CardTitle>
            <CardDescription>{t("dashboard.teamSubtitle")}</CardDescription>
          </div>
          <Link
            to={`/activity/now${teamId ? `?team=${teamId}` : ""}`}
            className="shrink-0 font-medium text-primary hover:underline"
          >
            {t("dashboard.workingNowLink")}
          </Link>
        </CardHeader>
        {status.isError ? (
          <div className="flex items-center gap-3 px-5 pb-5 text-danger">
            {t("common.error")}
            <Button variant="secondary" size="sm" onClick={() => void status.refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        ) : !status.isPending && items.length === 0 ? (
          <EmptyState
            icon={Users}
            title={t("dashboard.teamEmptyTitle")}
            description={t("dashboard.teamEmptyDescription")}
          />
        ) : (
          <TeamTable items={items} pending={status.isPending} lang={lang} />
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <TopAppsCard
          items={team?.top_apps}
          lang={lang}
          moreHref={appsHref}
          description={t("dashboard.thisWeek")}
        />
        <ServerStatusCard />
      </div>
    </div>
  );
}

function PersonalView({ data, lang }: { data: Dashboard | undefined; lang: string }) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const my = data?.me;
  const kpi = (value: string | undefined) => value ?? <Skeleton className="h-8 w-24" />;
  const vacationText = useVacationText();

  return (
    <div className="flex flex-col gap-6">
      {me.vacation && (
        <Alert variant="info" className="items-center">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex-1">
              {me.vacation.active
                ? t("dashboard.onVacation", { range: vacationText(me.vacation) })
                : t("dashboard.vacationPlanned", { range: vacationText(me.vacation) })}
            </span>
            <Button asChild variant="secondary" size="sm">
              <Link to="/time-off">{t("dashboard.vacationLink")}</Link>
            </Button>
          </div>
        </Alert>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t("dashboard.kpi.myToday")}
          icon={Clock}
          value={kpi(my && formatDuration(worked(my.today), lang))}
          hint={(my && manualHint(t, my.today, lang)) ?? t("dashboard.todayHint")}
        />
        <StatCard
          label={t("dashboard.kpi.hoursWeek")}
          icon={CalendarRange}
          value={kpi(my && formatDuration(worked(my.week), lang))}
          hint={(my && manualHint(t, my.week, lang)) ?? t("dashboard.weekHint")}
        />
        <StatCard
          label={t("dashboard.kpi.activityToday")}
          icon={Gauge}
          value={kpi(my && (my.today.tracked_seconds > 0 ? `${my.today.activity_percent}%` : "—"))}
          hint={my && t("dashboard.weekActivity", { percent: my.week.activity_percent })}
        />
        <StatCard
          label={t("dashboard.kpi.productive")}
          icon={Sparkles}
          value={kpi(
            my &&
              (my.today.tracked_seconds > 0
                ? `${percentOf(my.today.productive_seconds, my.today.tracked_seconds)}%`
                : "—"),
          )}
          hint={my && productiveHint(t, my.today)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle>{t("dashboard.myWeek")}</CardTitle>
            <CardDescription>{data ? formatWeekRange(data.week_start, lang) : " "}</CardDescription>
          </CardHeader>
          <div className="px-5 pb-5">
            {data && my ? (
              <WeekChart days={my.days} projects={data.projects} today={data.today} lang={lang} />
            ) : (
              <Skeleton className="h-64 w-full" />
            )}
          </div>
        </Card>
        <TopAppsCard
          items={my?.top_apps}
          lang={lang}
          moreHref={`/activity/apps?period=week&user=${me.id}`}
          description={t("dashboard.thisWeek")}
        />
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex-row items-start justify-between gap-3 pb-4">
          <div>
            <CardTitle>{t("dashboard.recentScreenshots")}</CardTitle>
            <CardDescription>{t("dashboard.thisWeek")}</CardDescription>
          </div>
          <Link to={`/activity/${me.id}`} className="shrink-0 font-medium text-primary hover:underline">
            {t("dashboard.openTimeline")}
          </Link>
        </CardHeader>
        {my && my.recent_screenshots.length === 0 ? (
          <p className="px-5 pb-5 text-muted">{t("dashboard.noScreenshots")}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 px-5 pb-5 sm:grid-cols-3 xl:grid-cols-6">
            {(my?.recent_screenshots ?? []).map((s) => (
              <Link
                key={s.id}
                to={`/activity/${me.id}?date=${s.taken_at.slice(0, 10)}`}
                className="flex flex-col gap-1.5"
              >
                <img
                  src={s.thumb_url}
                  alt={t("timeline.screenshotAlt", { time: formatTime(s.taken_at, lang, me.timezone) })}
                  className="aspect-[16/10] w-full rounded-md border object-cover"
                  loading="lazy"
                />
                <span className="text-xs text-muted tabular-nums">
                  {formatTime(s.taken_at, lang, me.timezone)}
                </span>
              </Link>
            ))}
            {!my && [0, 1, 2].map((i) => <Skeleton key={i} className="aspect-[16/10] w-full" />)}
          </div>
        )}
      </Card>
    </div>
  );
}
